/**
 * @file  surcharges/options.ts
 * @description  the surcharge options service to manage option selection
 *
 * @author ogarcia (ozkary)
 * @date 20220707 initial implementation
 */

/**
 * Imports.
 */
import { Surcharge } from '../../functions/quotes/quotes.model';
import * as _cache from '../cache';
import surchargesJson from './surcharges.json';

/**
 * define the cache keys
 * options - for selected options
 */
const optionsKey = _cache.Key.options;

/**
 * options private operations
 */

/**
 * List of valid surcharges
 */
export const validOptions = surchargesJson?.options ?? [];

/**
 * gets the list of options from the cache
 * @returns the list of options
 */
export const getItems = async (): Promise<Surcharge[]> => {
  let items = _cache.getItem<Surcharge[]>(optionsKey);

  if (!items || items === undefined) {
    items = [] as Surcharge[];
  }
  return items;
};

/**
 * saves the current surcharges to the cache
 * @returns the list of surcharges
 */
const setItems = async (items: Surcharge[]): Promise<Surcharge[]> => {
  _cache.setItem<Surcharge[]>(optionsKey, items);
  return items;
};

/**
 * add a new surcharge
 * @param item surcharge item
 * @returns the list of options
 */
const addItem = async (item: Surcharge): Promise<Surcharge[]> => {
  const items = await getItems();
  if (item && items) {
    const index = items.map((i) => i.Code).indexOf(item.Code); // lookup and replace when found
    if (index < 0) {
      items.splice(0, 0, item); // not found add it
    } else {
      item.Preselected = false;
      item.Previous = items[index].Previous;
      items[index] = item; // found replace it
    }
  }
  await setItems(items);
  return items;
};

/**
 * remove an option
 * @param item surcharge
 * @returns the list of options
 */
const removeItem = async (item: Surcharge): Promise<Surcharge[]> => {
  const items = await getItems();
  if (items && items.length > 0) {
    // find the item from the list and remove it
    const index = items.map((i) => i.Code).indexOf(item.Code);
    if (index > -1) {
      items[index].IsRemoved = true; // soft delete the item
      items[index].Preselected = false;
    }
  }
  await setItems(items);
  return items;
};

/**
 * options exports
 */

/**
 * clear all the selected options
 */
export const clearOptions = async (): Promise<void> => {
  _cache.removeItem(optionsKey);
};

/**
 * options operations
 */
export const addOption = async (item: Surcharge): Promise<Surcharge[]> => {
  return await addItem(item);
};

/**
 * removes (soft-deletes) the options and returns the remaining options
 * IsRemoved is set to true to be able to track and recoved if needed
 * @param item the option to remove
 * @returns the list of options
 */
export const removeOption = async (item: Surcharge): Promise<Surcharge[]> => {
  return await removeItem(item);
};

/**
 * Adds a list of preselected options. This is used when a previous contract
 * has some options. Each item added will be set to the
 * @param items a list of surcharges
 * @param reset true to clear all the options
 * @returns the selected options
 */
export const addPreselectedOptions = async (
  items: Surcharge[],
  reset = true
): Promise<Surcharge[]> => {
  if (validOptions && validOptions.length > 0) {
    items = items.filter((opt) => validOptions.indexOf(opt?.Code || '') > -1);
  }

  let newList = items;

  if (items && items?.length > 0) {
    if (reset) {
      await clearOptions();
    }
    const newItems = items.map((i) => {
      return { ...i, Preselected: true, Previous: true };
    });
    const current: Surcharge[] = await getItems();
    newList = [...current, ...newItems];
    await setItems(newList);
  }
  return newList;
};

/**
 * returns the selected options
 * It does not retun soft-deleted items IsRemoved = true
 * @param includePreselected true returns all options even if pre-selected. once the user selects the preselected prop is removed
 */
export const selectedOptions = async (
  includePreselected = true
): Promise<Surcharge[]> => {
  let items = await getItems();
  if (items && items.length > 0) {
    // remove any soft deletes
    items = items.filter(
      (i) => i.IsRemoved === undefined || i.IsRemoved === false
    );
    // remove pre-selected items
    if (!includePreselected) {
      items = items.filter(
        (i) => i.Preselected === undefined || i.Preselected === false
      );
    }
  }
  return items;
};

/**
 * Use to reset all the operations done by the user by clearing the cache, but also
 * recovering the pre-selected items that were removed on a soft-delete operation
 * @param keepPreselected true to not remove the pres-selected option
 */
export const resetOptions = async (
  keepPreselected = true
): Promise<Surcharge[] | void> => {
  if (keepPreselected) {
    // keep the preselected options
    let items = await getItems();
    items = items
      .filter((i) => i.Preselected === true)
      .map((i) => {
        return { ...i, IsRemoved: false };
      });
    await setItems(items);
    return items;
  } else {
    await clearOptions();
  }
};

/**
 * updates one or more options from the selected list of options and compares to the new options list
 * This should avoid showing selected options that are not longer available
 * @param planOptions list of options
 */
export const updateOptions = async (
  planOptions: Surcharge[]
): Promise<Surcharge[]> => {
  let currentOptions = await getItems();
  if (currentOptions?.length > 0 && planOptions !== null) {
    // find the options that are included in the plan. need to get the new price
    const foundOptions = currentOptions.map((i) => {
      let option = planOptions?.find((o) => o.Code === i.Code);
      if (option === undefined) {
        i.IsRemoved = true;
        option = i;
      } else {
        // keep as removed only if user removed it. re-store for coverage changes
        option.IsRemoved = i.IsRemoved && i.Preselected ? false : i.IsRemoved;
        option.Preselected = i.Preselected;
        option.Previous = i.Previous;
      }
      return option;
    });
    await setItems(foundOptions);
    currentOptions = foundOptions.filter((i) => i.IsRemoved === undefined || i.IsRemoved === false);
  }
  return currentOptions;
};
